import * as React from "react";

import {
  ActionService,
  ImageRegistryService,
  TriggerService,
} from "@/shared/domain/actions";
import { AdminService } from "@/shared/domain/admin";
import { AgentService } from "@/shared/domain/agents";
import { AgentThreadService } from "@/shared/domain/agentThread";
import { CollectionService } from "@/shared/domain/collections";
import { CommentService } from "@/shared/domain/comments";
import { ConfigService } from "@/shared/domain/config";
import { CustomFieldService } from "@/shared/domain/customFields";
import { DatasetService } from "@/shared/domain/datasets";
import { DeviceService } from "@/shared/domain/devices";
import { EventService } from "@/shared/domain/events";
import { ExperienceService } from "@/shared/domain/experiences";
import { FeatureFlagService } from "@/shared/domain/featureFlags";
import { FileService } from "@/shared/domain/files";
import { HealthService } from "@/shared/domain/health";
import { IntegrationService } from "@/shared/domain/integrations";
import { InviteService } from "@/shared/domain/invites";
import { LayoutService } from "@/shared/domain/layouts";
import { LimitService } from "@/shared/domain/limits/LimitService";
import { McpRegistryService } from "@/shared/domain/mcp";
import { MeteringService } from "@/shared/domain/metering/MeteringService";
import { MetricsService } from "@/shared/domain/metrics";
import { NotificationService } from "@/shared/domain/notifications";
import { OrgService } from "@/shared/domain/orgs";
import { QueryService } from "@/shared/domain/query";
import { SecretService } from "@/shared/domain/secrets/SecretService";
import { SessionService } from "@/shared/domain/sessions";
import { SkillService } from "@/shared/domain/skills";
import { TokenService } from "@/shared/domain/tokens";
import { TopicService } from "@/shared/domain/topics";
import { UserGroupService } from "@/shared/domain/userGroups";
import { UserService } from "@/shared/domain/users/UserService";
import { WorkspaceService } from "@/shared/domain/workspaces/WorkspaceService";

import { useHttpClient } from "../HttpClient";

import { DomainServicesContext } from "./DomainServicesContext";

export function DomainServicesProvider({ children }: React.PropsWithChildren) {
  const { httpClient } = useHttpClient();

  // Keys are ordered alphabetically to match the `Context` interface
  // and `defaultDomainContextFactory` in `DomainServicesContext.ts`.
  const domainServicesContext = React.useMemo(() => {
    return {
      actionService: new ActionService(httpClient),
      adminService: new AdminService(httpClient),
      agentService: new AgentService(httpClient),
      agentThreadService: new AgentThreadService(httpClient),
      collections: new CollectionService(httpClient),
      commentService: new CommentService(httpClient),
      configService: new ConfigService(httpClient),
      customFieldService: new CustomFieldService(httpClient),
      datasetService: new DatasetService(httpClient),
      devices: new DeviceService(httpClient),
      events: new EventService(httpClient),
      experienceService: new ExperienceService(httpClient),
      featureFlagService: new FeatureFlagService(httpClient),
      files: new FileService(httpClient),
      healthService: new HealthService(httpClient),
      imageRegistryService: new ImageRegistryService(httpClient),
      integrationService: new IntegrationService(httpClient),
      inviteService: new InviteService(httpClient),
      layoutService: new LayoutService(httpClient),
      limitSummary: new LimitService(httpClient),
      mcpRegistryService: new McpRegistryService(httpClient),
      meteringService: new MeteringService(httpClient),
      metricsService: new MetricsService(httpClient),
      notifications: new NotificationService(httpClient),
      orgService: new OrgService(httpClient),
      queryService: new QueryService(httpClient),
      secretService: new SecretService(httpClient),
      sessionService: new SessionService(httpClient),
      skillService: new SkillService(httpClient),
      tokens: new TokenService(httpClient),
      topics: new TopicService(httpClient),
      triggerService: new TriggerService(httpClient),
      userGroupService: new UserGroupService(httpClient),
      users: new UserService(httpClient),
      workspaceService: new WorkspaceService(httpClient),
    };
  }, [httpClient]);

  return (
    <DomainServicesContext.Provider value={domainServicesContext}>
      {children}
    </DomainServicesContext.Provider>
  );
}
